import { Alert, Stack, Text, Group } from '@mantine/core';
import { Check, Circle, Timer, WarningCircle } from '@phosphor-icons/react';
import { PASSWORD_RULES } from '../../utils/passwordPolicy';
import type { AuthThrottle } from '../../hooks/useAuthThrottle';

/** Live checklist of the new-password policy, shown while the user types. */
export function PasswordPolicyChecklist({ password }: { password: string }) {
  return (
    <Stack gap={4} mt={-6}>
      {PASSWORD_RULES.map((rule) => {
        const ok = rule.test(password);
        return (
          <Group key={rule.key} gap={6} wrap="nowrap">
            {ok ? (
              <Check size={12} weight="bold" color="var(--green)" />
            ) : (
              <Circle size={12} color="var(--text-secondary)" />
            )}
            <Text size="xs" c={ok ? 'var(--green)' : 'var(--text-secondary)'}>
              {rule.label}
            </Text>
          </Group>
        );
      })}
      <Text size="xs" c="var(--text-secondary)" fs="italic">
        Common passwords and passwords containing your username or email are rejected.
      </Text>
    </Stack>
  );
}

/** Shows the 429 AUTH_THROTTLED message with a live countdown. Renders nothing when not locked. */
export function ThrottleAlert({ throttle }: { throttle: AuthThrottle }) {
  if (!throttle.isLocked) return null;
  return (
    <Alert
      icon={<Timer size={16} />}
      color="orange"
      styles={{ root: { backgroundColor: 'var(--warn-dim)', borderColor: 'var(--warn-border)' } }}
    >
      <Text size="sm" c="var(--warning)">{throttle.message}</Text>
      <Text size="xs" c="var(--text-secondary)" mt={4}>
        Try again in <strong style={{ fontFamily: 'Geist Mono, monospace' }}>{throttle.countdown}</strong>
      </Text>
    </Alert>
  );
}

/** Error alert that renders multi-line (DTO validation) messages one per line. */
export function ErrorAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <Alert
      icon={<WarningCircle size={16} />}
      color="red"
      styles={{ root: { backgroundColor: 'var(--danger-dim)', borderColor: 'var(--danger-border)', color: 'var(--danger)' } }}
    >
      <Text size="sm" c="var(--danger)" style={{ whiteSpace: 'pre-line' }}>{message}</Text>
    </Alert>
  );
}
